import type { ICompasAIChatAction, ICompasAIChatHistoryResponse } from "@/types/compass.types";

export type QueueStatusMessageType = "initial" | "queue_snapshot" | "update";

export const QUEUE_TASK_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  "initial",
  "queue_snapshot",
  "update",
]);

/** Auth rejection — copy web TERMINAL_CLOSE_CODES. Do not reconnect. */
export const TERMINAL_CLOSE_CODES: ReadonlySet<number> = new Set([4001, 1008]);

export const POLL_INTERVAL_MS = 4000;
export const MAX_RECONNECT_ATTEMPTS = 8;
export const BASE_RECONNECT_DELAY_MS = 1000;
export const MAX_RECONNECT_DELAY_MS = 30_000;

const ACTIVE_STATUSES = new Set(["queued", "processing", "pending"]);
const TERMINAL_STATUSES = new Set(["processed", "completed", "failed", "cancelled"]);

/** pending ≡ queued, completed ≡ processed. */
export function normalizeStatus(status: string): string {
  if (status === "pending") return "queued";
  if (status === "processed") return "completed";
  return status;
}

export function isActiveStatus(status: string): boolean {
  return ACTIVE_STATUSES.has(status);
}

export function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function isToastableTerminal(status: string): "success" | "error" | null {
  const normalized = normalizeStatus(status);
  if (normalized === "completed") return "success";
  if (normalized === "failed") return "error";
  return null;
}

export function shouldReconnectOnClose(code: number): boolean {
  return !TERMINAL_CLOSE_CODES.has(code);
}

export function nextReconnectDelay(attempt: number): number {
  return Math.min(BASE_RECONNECT_DELAY_MS * 2 ** attempt, MAX_RECONNECT_DELAY_MS);
}

export function buildSocketUrl(ticket: string, contextId: string): string {
  const apiUrl = process.env.API_BASE_URL ?? "http://localhost:8000";
  const url = new URL(apiUrl);
  const wsProtocol = url.protocol === "https:" ? "wss:" : "ws:";
  const socketUrl = new URL(`${wsProtocol}//${url.host}/compas-ai/ws/queue-status`);
  socketUrl.searchParams.set("ticket", ticket);
  socketUrl.searchParams.set("context_id", contextId);
  return socketUrl.toString();
}

export interface QueueStatusMessage {
  type: QueueStatusMessageType;
  tasks: ICompasAIChatAction[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value != null;
}

/**
 * `keyless` is an entry the backend is allowed to send: the web client's
 * `normalizeQueueTask` also drops a task with neither `queue_item_id` nor
 * `id`. `malformed` is a wrongly typed field, which means the payload
 * itself cannot be trusted.
 */
type TaskVerdict = "valid" | "keyless" | "malformed";

/**
 * The socket payload is untrusted. Require a usable key plus well-typed
 * optional fields so `taskKey` and the status merges cannot throw.
 */
function classifyQueueTask(value: unknown): TaskVerdict {
  if (!isRecord(value)) return "malformed";
  const { id, queue_item_id: queueItemId, status } = value;
  if (id !== undefined && typeof id !== "string") return "malformed";
  if (queueItemId !== undefined && typeof queueItemId !== "string") return "malformed";
  if (status !== undefined && status !== null && typeof status !== "string") return "malformed";
  const key = queueItemId ?? id;
  return typeof key === "string" && key !== "" ? "valid" : "keyless";
}

export function parseQueueMessage(raw: string): QueueStatusMessage | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    const type = parsed.type;
    const tasks = parsed.tasks;
    if (typeof type !== "string" || !QUEUE_TASK_MESSAGE_TYPES.has(type)) return null;
    if (!Array.isArray(tasks)) return null;
    const validTasks: ICompasAIChatAction[] = [];
    let hasMalformed = false;
    for (const task of tasks) {
      const verdict = classifyQueueTask(task);
      if (verdict === "malformed") hasMalformed = true;
      else if (verdict === "valid") validTasks.push(task as ICompasAIChatAction);
    }
    // `initial` and `queue_snapshot` replace local state, so a corrupt entry
    // would prune a live task and stop its polling. Reject the whole snapshot
    // and wait for the next one. An `update` only merges, so filtering is safe.
    if (hasMalformed && type !== "update") return null;
    // A payload whose entries are all unusable is corrupt, not an empty
    // queue. Drop it rather than let a snapshot merge prune live statuses.
    if (validTasks.length === 0 && tasks.length > 0) return null;
    return { type: type as QueueStatusMessageType, tasks: validTasks };
  } catch {
    return null;
  }
}

export function taskKey(task: ICompasAIChatAction): string | undefined {
  const key = task.queue_item_id ?? task.id;
  return key !== "" ? key : undefined;
}

export interface TaskUpdateResult {
  currentStatuses: Record<string, string>;
  hasCompletion: boolean;
  completedCount: number;
  failedCount: number;
  processingTasks: ICompasAIChatAction[];
  wasProcessing: boolean;
}

export function mergeSnapshotStatuses(
  previous: Record<string, string>,
  snapshotTasks: ICompasAIChatAction[]
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const task of snapshotTasks) {
    const key = taskKey(task);
    if (key !== undefined && task.status != null) {
      next[key] = normalizeStatus(task.status);
    }
  }
  for (const [id, status] of Object.entries(previous)) {
    if (!(id in next) && isTerminalStatus(status)) {
      next[id] = status;
    }
  }
  return next;
}

export function mergeDeltaStatuses(
  previous: Record<string, string>,
  tasks: ICompasAIChatAction[]
): Record<string, string> {
  const next = { ...previous };
  for (const task of tasks) {
    const key = taskKey(task);
    if (key !== undefined && task.status != null) {
      next[key] = normalizeStatus(task.status);
    }
  }
  return next;
}

export function countStatusTransitions(
  previous: Record<string, string>,
  current: Record<string, string>
): Pick<TaskUpdateResult, "hasCompletion" | "completedCount" | "failedCount"> {
  let hasCompletion = false;
  let completedCount = 0;
  let failedCount = 0;
  for (const [id, status] of Object.entries(current)) {
    const prev = previous[id];
    // Skip hydration (first sighting) and already-terminal rows. Toast/invalidate
    // only on a real transition into completed/failed from a known active status.
    if (prev == null || isTerminalStatus(prev) || !isTerminalStatus(status)) continue;
    hasCompletion = true;
    const kind = isToastableTerminal(status);
    if (kind === "success") completedCount += 1;
    if (kind === "error") failedCount += 1;
  }
  for (const [id, status] of Object.entries(previous)) {
    if (isActiveStatus(status) && !(id in current)) {
      hasCompletion = true;
    }
  }
  return { hasCompletion, completedCount, failedCount };
}

export function processTaskUpdate(
  tasks: ICompasAIChatAction[],
  previousStatuses: Record<string, string>,
  mode: "snapshot" | "delta" = "delta"
): TaskUpdateResult {
  const wasProcessing = Object.values(previousStatuses).some(isActiveStatus);
  const currentStatuses =
    mode === "snapshot"
      ? mergeSnapshotStatuses(previousStatuses, tasks)
      : mergeDeltaStatuses(previousStatuses, tasks);
  const transitions = countStatusTransitions(previousStatuses, currentStatuses);
  const processingTasks = tasks.filter(
    (task) => normalizeStatus(task.status ?? "") === "processing"
  );
  return { currentStatuses, ...transitions, processingTasks, wasProcessing };
}

export function extractActionsFromHistory(
  history: ICompasAIChatHistoryResponse | undefined
): ICompasAIChatAction[] {
  if (history?.history == null || history.history.length === 0) return [];
  return history.history.filter((item): item is ICompasAIChatAction => item.type === "action");
}

export function historyHasActiveActions(
  history: ICompasAIChatHistoryResponse | undefined
): boolean {
  return extractActionsFromHistory(history).some(
    (action) => action.status != null && isActiveStatus(action.status)
  );
}

export function getHistoryRefetchInterval(
  history: ICompasAIChatHistoryResponse | undefined
): number | false {
  return historyHasActiveActions(history) ? POLL_INTERVAL_MS : false;
}
